import { useState, useEffect, useRef } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useNavigate } from 'react-router-dom';

interface LoginModalProps {
  onSuccess?: () => void;
}

export default function LoginModal({ onSuccess }: LoginModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { login } = useUser();
  const navigate = useNavigate();

  // Auto-focus input when modal opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('请输入您的姓名');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/user/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || '登录失败，请重试');
        return;
      }

      // Check if user is admin
      if (data.data.user.isAdmin) {
        // Admin login - save token and redirect to admin dashboard
        localStorage.setItem('auth_token', data.data.user.token);
        navigate('/admin');
        return;
      }

      // Regular user login
      login(data.data.user.name, data.data.user.seat);
      onSuccess?.();
    } catch (err) {
      setError('网络连接失败，请检查您的网络');
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#C41E3A] to-[#D4AF37] rounded-full mx-auto mb-4 flex items-center justify-center">
            <span className="material-symbols-outlined text-white text-3xl">person</span>
          </div>
          <h2 className="text-2xl font-medium text-[#2d2d2d] mb-2">欢迎参加年会</h2>
          <p className="text-[#666666] text-sm">请输入您的姓名以继续</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm text-[#666666] mb-2">
              姓名
            </label>
            <input
              ref={inputRef}
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="请输入您的姓名"
              className="input"
              disabled={isLoading}
              autoComplete="name"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !name.trim()}
            className="btn-primary w-full py-3 text-sm tracking-wider"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="spinner w-4 h-4 border-white/30 border-t-white"></span>
                登录中...
              </span>
            ) : (
              '登录'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-[#999999]">
            请确认您的姓名在与会名单中
          </p>
        </div>
      </div>
    </div>
  );
}
